import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import {
  AlertCircle,
  Bot,
  ChevronDown,
  Loader2,
  Paperclip,
  Send,
} from 'lucide-react';
import toast from 'react-hot-toast';

import ChatBubble from '../components/dedicated/ChatBubble';
import TypingIndicator from '../components/dedicated/TypingIndicator';
import WorkflowMessage from '../components/dedicated/WorkflowMessage';
import MediaUpload from '../components/dedicated/MediaUpload';
import BottomModal from '../components/dedicated/BottomModal';
import {
  chatbotWidgetService,
  type ChatbotWidgetInitResponse,
} from '../services/chatbotWidget.service';
import type { WorkflowMessage as WorkflowMessageType } from '../types/workflow.types';
import { useChatbotId } from '@/hooks/useChatbotId';

interface Message {
  id: string;
  content: string;
  isFromUser: boolean;
  createdAt: Date;
  type: string;
  sources?: any[];
  mediaUrl?: string;
  workflowData?: WorkflowMessageType | any;
  location?: { latitude: number; longitude: number; address?: string };
}

interface MediaUploadResult {
  media: {
    url: string;
    mimeType: string;
    originalName: string;
  };
}

type ConversationStatus = 'bot_active' | 'pending_agent' | 'agent_active' | 'closed';

const poweredByIcon = '/images/poweredBy.webp';

const getOrCreateVisitorId = () => {
  if (typeof window === 'undefined') return '';

  const storageKey = 'visitorId';
  const existingId = localStorage.getItem(storageKey);
  if (existingId) return existingId;

  const newId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  localStorage.setItem(storageKey, newId);
  return newId;
};

const getSocketURL = () => {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  return apiUrl.replace(/\/api\/?$/, '');
};

const getMessageTypeFromMime = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
};

const formatMessage = (msg: any): Message => {
  const workflowData = msg.workflowData || (msg.type === 'workflow' ? msg : null);
  const type = msg.messageType || msg.type || 'text';

  return {
    id: msg._id || msg.id || `msg_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    content: msg.content || workflowData?.text || workflowData?.body || '',
    isFromUser: msg.isFromUser || false,
    createdAt: new Date(msg.createdAt || Date.now()),
    type,
    mediaUrl: msg.mediaUrl,
    sources: msg.sources || [],
    workflowData,
    location: msg.location || null,
  };
};

const QuickQuestion = ({
  question,
  onClick,
  disabled,
}: {
  question: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-full text-left px-4 py-2 mb-2 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-sm text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {question}
  </button>
);

export default function ChatbotDedicatedPage() {
  const botId = useChatbotId();
  const [visitorId] = useState(getOrCreateVisitorId);
  const [config, setConfig] = useState<ChatbotWidgetInitResponse | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationStatus, setConversationStatus] = useState<ConversationStatus>('bot_active');
  const [inputValue, setInputValue] = useState('');
  const [isBooting, setIsBooting] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [showDataCollection, setShowDataCollection] = useState(false);
  const [collectedData, setCollectedData] = useState<Record<string, string>>({});
  const [pendingMessage, setPendingMessage] = useState('');
  const [isSubmittingData, setIsSubmittingData] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const primaryColor = config?.primaryColor || '#3B82F6';
  const secondaryColor = config?.secondaryColor || primaryColor;
  const textColor = config?.textColor || '#ffffff';
  const isAgentMode = conversationStatus === 'agent_active' || conversationStatus === 'pending_agent';
  const needsDataCollection = Boolean(
    config?.dataCollection?.length && !config.dataCompletionCompleted
  );

  const sizeClass = useMemo(() => {
    const sizes: Record<string, string> = {
      small: 'w-[90vw] md:w-[25vw] h-[50vh] md:h-[50vh]',
      medium: 'w-[94vw] md:w-[45vw] xl:w-[30vw] 2xl:w-[30vw] h-[92vh] md:h-[calc(100vh-150px)]',
      large: 'w-[94vw] md:w-[45vw] xl:w-[55vw] h-[75vh] md:h-[80vh] max-h-[75vh]',
    };

    return sizes[config?.size || 'medium'] || sizes.medium;
  }, [config?.size]);

  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, scrollToBottom]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (!isLoading && !showDataCollection) {
      window.setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLoading, showDataCollection]);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollBottom(scrollHeight - scrollTop - clientHeight >= 10);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!botId || !visitorId) return;

    setIsBooting(true);
    chatbotWidgetService
      .initialize(botId, visitorId)
      .then((data) => {
        setConfig(data);
        setConversationId(data.conversationId || null);
        setConversationStatus(data.conversationStatus || 'bot_active');
        setMessages((data.messages || []).map(formatMessage));
      })
      .catch(() => {
        toast.error('Failed to load chatbot configuration');
      })
      .finally(() => setIsBooting(false));
  }, [botId, visitorId]);

  useEffect(() => {
    if (!visitorId) return;

    const socketURL = getSocketURL();
    if (!socketURL) return;

    const socket = io(socketURL, {
      reconnectionDelay: 1000,
      reconnection: true,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      timeout: 20000,
      path: '/socket.io/',
      query: { visitorId, type: 'visitor' },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join:visitor', visitorId);
      if (conversationIdRef.current) socket.emit('join:conversation', conversationIdRef.current);
    });

    socket.on('reconnect', () => {
      socket.emit('join:visitor', visitorId);
      if (conversationIdRef.current) socket.emit('join:conversation', conversationIdRef.current);
    });

    socket.on('message:new', (msg: any) => {
      addMessage(formatMessage(msg));
      if (msg.sender === 'agent' || msg.sender === 'ai' || !msg.isFromUser) {
        setIsLoading(false);
        setLoadingStatus(null);
      }
    });

    socket.on('conversation:status', ({ status }) => {
      setConversationStatus(status as ConversationStatus);
    });

    socket.on('workflow:timeout', (workflowResult: any) => {
      addMessage(formatMessage({
        _id: `workflow_${workflowResult.sessionId}_${Date.now()}`,
        messageType: 'workflow',
        type: 'workflow',
        workflowData: workflowResult,
        isFromUser: false,
        createdAt: new Date(),
        content: workflowResult.text || workflowResult.body || '',
      }));
    });

    socket.on('workflow:next', (workflowResult: any) => {
      addMessage(formatMessage({
        _id: `workflow_${workflowResult.sessionId}_${Date.now()}`,
        messageType: 'workflow',
        type: 'workflow',
        workflowData: workflowResult,
        isFromUser: false,
        createdAt: new Date(),
        content: workflowResult.text || workflowResult.body || '',
      }));
    });

    socket.on('message:workflow', (msg: any) => {
      addMessage(formatMessage(msg));
      setIsLoading(false);
      setLoadingStatus(null);
    });

    socket.on('message:processing', ({ statusText }) => {
      setLoadingStatus(statusText);
      setIsLoading(true);
    });

    socket.on('message:bot:chunk', ({ chunk }) => {
      setStreamingMessage((prev) => prev + chunk);
    });

    socket.on('message:complete', (data: any = {}) => {
      setStreamingMessage((current) => {
        if (current) {
          addMessage({
            id: `bot_${Date.now()}`,
            content: current,
            isFromUser: false,
            createdAt: new Date(),
            type: 'text',
            sources: data.sources || [],
          });
        }
        return '';
      });
      setIsLoading(false);
      setLoadingStatus(null);
    });

    socket.on('message:error', ({ error }) => {
      toast.error(error);
      setIsLoading(false);
      setLoadingStatus(null);
      setStreamingMessage('');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [addMessage, visitorId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.connected && conversationId) {
      socket.emit('join:conversation', conversationId);
    }
  }, [conversationId]);

  const startBotLoading = () => {
    if (!isAgentMode) setIsLoading(true);
  };

  const handleStartConversation = async () => {
    if (!botId) return;

    setIsStartingConversation(true);
    try {
      const result = await chatbotWidgetService.startConversation(botId, visitorId);
      if (result?.conversationId) {
        setConversationId(result.conversationId);
        socketRef.current?.emit('join:conversation', result.conversationId);
      }
    } catch {
      toast.error('Failed to start conversation');
    } finally {
      setIsStartingConversation(false);
    }
  };

  const sendMessageDirectly = async (text: string) => {
    if (!botId) return;

    setStreamingMessage((current) => {
      if (current) {
        addMessage({
          id: `bot_${Date.now()}`,
          content: current,
          isFromUser: false,
          createdAt: new Date(),
          type: 'text',
        });
      }
      return '';
    });

    addMessage({
      id: `msg_${Date.now()}`,
      content: text,
      isFromUser: true,
      createdAt: new Date(),
      type: 'text',
    });

    startBotLoading();

    try {
      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'text',
        text,
      });
    } catch {
      toast.error('Failed to send message');
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    if (needsDataCollection) {
      setPendingMessage(text);
      setInputValue('');
      setShowDataCollection(true);
      return;
    }

    setInputValue('');
    await sendMessageDirectly(text);
  };

  const handleDataCollectionSubmit = async () => {
    if (!botId || !config?.dataCollection) return;

    const allRequiredFilled = config.dataCollection
      .filter((field) => field.required)
      .every((field) => collectedData[field._id]?.trim());

    if (!allRequiredFilled) {
      toast.error('Please fill in all required fields');
      return;
    }

    const formattedCollectedData = config.dataCollection.reduce<Record<string, string>>((acc, field) => {
      const value = collectedData[field._id];
      if (value !== undefined) acc[field.name] = value;
      return acc;
    }, {});

    setIsSubmittingData(true);
    try {
      await chatbotWidgetService.submitDataCollection(botId, {
        visitorId,
        collectedData: formattedCollectedData,
      });

      setConfig((prev) => prev ? { ...prev, dataCompletionCompleted: true } : prev);
      setShowDataCollection(false);

      if (pendingMessage) {
        const message = pendingMessage;
        setPendingMessage('');
        await sendMessageDirectly(message);
      }
    } catch {
      toast.error('Failed to submit information');
    } finally {
      setIsSubmittingData(false);
    }
  };

  const handleWorkflowButtonClick = async (buttonId: string, buttonLabel?: string) => {
    if (!botId || isLoading) return;

    if (buttonLabel) {
      addMessage({
        id: `msg_${Date.now()}`,
        content: buttonLabel,
        isFromUser: true,
        createdAt: new Date(),
        type: 'text',
      });
    }

    startBotLoading();

    try {
      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'button',
        buttonId,
        buttonLabel,
        text: buttonLabel,
      });
    } catch {
      toast.error('Failed to send button click');
      setIsLoading(false);
    }
  };

  const handleWorkflowListSelect = async (itemId: string, itemTitle?: string) => {
    if (!botId || isLoading) return;

    if (itemTitle) {
      addMessage({
        id: `msg_${Date.now()}`,
        content: itemTitle,
        isFromUser: true,
        createdAt: new Date(),
        type: 'text',
      });
    }

    startBotLoading();

    try {
      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'list_reply',
        listItemId: itemId,
        itemTitle,
        text: itemTitle,
      });
    } catch {
      toast.error('Failed to send list selection');
      setIsLoading(false);
    }
  };

  const handleWorkflowAddressSubmit = async (address: string) => {
    if (!botId || isLoading) return;

    addMessage({
      id: `msg_${Date.now()}`,
      content: address,
      isFromUser: true,
      createdAt: new Date(),
      type: 'text',
    });

    startBotLoading();

    try {
      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'text',
        text: address,
      });
    } catch {
      toast.error('Failed to send address');
      setIsLoading(false);
    }
  };

  const handleWorkflowLocationCapture = async (latitude: number, longitude: number) => {
    if (!botId || isLoading) return;

    addMessage({
      id: `msg_${Date.now()}`,
      content: `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
      isFromUser: true,
      createdAt: new Date(),
      type: 'location',
      location: { latitude, longitude },
    });

    startBotLoading();

    try {
      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'location',
        location: { latitude, longitude },
      });
    } catch {
      toast.error('Failed to send location');
      setIsLoading(false);
    }
  };

  const handleMediaUpload = async (file: File) => {
    if (!botId) return;

    try {
      const uploadResult = await chatbotWidgetService.uploadMedia(
        file,
        visitorId,
        botId,
        conversationId
      ) as MediaUploadResult;

      const messageType = getMessageTypeFromMime(uploadResult.media.mimeType);

      addMessage({
        id: `msg_${Date.now()}`,
        content: uploadResult.media.originalName,
        isFromUser: true,
        createdAt: new Date(),
        type: messageType,
        mediaUrl: uploadResult.media.url,
      });

      startBotLoading();

      await chatbotWidgetService.sendMessage(conversationId, visitorId, botId, {
        type: 'media',
        media: {
          url: uploadResult.media.url,
          mimeType: uploadResult.media.mimeType,
          caption: uploadResult.media.originalName,
        },
      });

      setShowMediaUpload(false);
    } catch {
      toast.error('Failed to upload media');
      setIsLoading(false);
      throw new Error('Failed to upload media');
    }
  };

  if (isBooting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-600 to-gray-400 flex items-center justify-center mb-5 shadow-lg">
          <Bot size={30} className="text-white" />
        </div>
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin mb-3" />
        <p className="text-gray-600 font-medium text-sm tracking-wide">Initializing secure chat...</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <h1 className="text-xl font-bold text-gray-800">Chatbot Not Found</h1>
        <p className="text-gray-500 text-sm">The chatbot you are looking for does not exist or is unavailable.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-3 md:p-8">
      <div
        id="chatbot-dedicated-container"
        className={`relative border-2 rounded-xl shadow-2xl flex flex-col overflow-hidden ${sizeClass} transition-all duration-500`}
        style={{
          transformOrigin: 'center center',
          borderColor: primaryColor,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        }}
      >
        <header
          className="px-4 py-3 flex items-center justify-between"
          style={{
            color: textColor,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center shrink-0">
              {config.logo ? (
                <img
                  src={config.logo}
                  alt="Bot logo"
                  className="w-8 h-8 object-cover"
                  onError={(event) => {
                    event.currentTarget.src = poweredByIcon;
                  }}
                />
              ) : (
                <Bot size={22} />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-lg truncate" style={{ color: textColor }}>
                {config.name}
              </h1>
              <p className="text-gray-200 text-xs flex gap-1 items-center">
                <span className="w-2.5 h-2.5 border-2 border-gray-100 bg-green-500 rounded-full" />
                Online
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 relative overflow-hidden">
          {isAgentMode && (
            <div className="shrink-0 px-4 py-2 text-sm text-white bg-transparent">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full animate-pulse text-white bg-green-500" />
                {conversationStatus === 'pending_agent'
                  ? 'Waiting for an agent to join...'
                  : 'Connected with an agent'}
              </div>
            </div>
          )}

          <div
            ref={messageContainerRef}
            className="absolute inset-0 overflow-y-auto p-4 bg-gray-100 rounded-t-xl"
            style={{ top: isAgentMode ? '40px' : '0' }}
          >
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-white shadow-md flex items-center justify-center mx-auto mb-4">
                  {config.logo ? (
                    <img
                      src={config.logo}
                      alt="Bot logo"
                      className="w-8 h-8 object-cover"
                      onError={(event) => {
                        event.currentTarget.src = poweredByIcon;
                      }}
                    />
                  ) : (
                    <Bot size={32} className="text-gray-400" />
                  )}
                </div>
                <h2 className="font-semibold text-lg mb-2 text-gray-900">{config.welcomeTitle}</h2>
                <p className="text-gray-600 text-sm mb-6">{config.welcomeMessage}</p>

                {!conversationId && (
                  <div className="flex justify-center mt-6 mb-2">
                    <button
                      onClick={handleStartConversation}
                      className="px-6 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center min-w-[160px] mx-auto disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: primaryColor }}
                      disabled={isStartingConversation}
                    >
                      {isStartingConversation ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Starting...
                        </span>
                      ) : (
                        'Start Conversation'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {conversationId && config.questionList && config.questionList.length > 0 && (
              <div className={messages.length === 0 ? 'mt-6' : 'mb-4'}>
                <p className="text-gray-600 text-sm mb-3">Quick questions:</p>
                {config.questionList.map((question, index) => (
                  <QuickQuestion
                    key={`${question}-${index}`}
                    question={question}
                    onClick={() => handleSendMessage(question)}
                    disabled={isLoading}
                  />
                ))}
              </div>
            )}

            {messages.map((message, index) => {
              if (message.type === 'workflow' && message.workflowData) {
                return (
                  <div key={message.id || index} className="flex justify-start mb-4">
                    <div className="flex items-start max-w-[80%] min-w-0">
                      <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-2 bg-gray-200 overflow-hidden">
                        {config.logo ? (
                          <img
                            src={config.logo}
                            alt="Bot logo"
                            className="w-6 h-6 object-cover"
                            onError={(event) => {
                              event.currentTarget.src = poweredByIcon;
                            }}
                          />
                        ) : (
                          <Bot size={16} className="text-gray-600" />
                        )}
                      </div>
                      <div className="rounded-xl rounded-tl-none bg-white shadow-sm border border-gray-100 max-w-full overflow-hidden">
                        <WorkflowMessage
                          message={message.workflowData}
                          onButtonClick={handleWorkflowButtonClick}
                          onListItemSelect={handleWorkflowListSelect}
                          onMediaUpload={handleMediaUpload}
                          onAddressSubmit={handleWorkflowAddressSubmit}
                          onLocationCapture={handleWorkflowLocationCapture}
                          disabled={
                            (index === messages.length - 1 && isLoading) ||
                            messages.slice(index + 1).some((item) => item.isFromUser)
                          }
                          config={config}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              return <ChatBubble key={message.id || index} message={message} config={config} />;
            })}

            {streamingMessage && (
              <div className="flex justify-start mb-4">
                <div className="flex items-start max-w-[80%]">
                  <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-2 bg-gray-200 overflow-hidden">
                    {config.logo ? (
                      <img
                        src={config.logo}
                        alt="Bot logo"
                        className="w-6 h-6 object-cover"
                        onError={(event) => {
                          event.currentTarget.src = poweredByIcon;
                        }}
                      />
                    ) : (
                      <Bot size={16} className="text-gray-600" />
                    )}
                  </div>
                  <div className="rounded-lg px-4 py-2 bg-gray-200">
                    <div className="text-sm prose prose-sm max-w-none prose-invert text-gray-800">
                      <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isLoading && !streamingMessage && <TypingIndicator config={config} />}

            <div ref={messagesEndRef} />
          </div>

          {showScrollBottom && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 text-blue-500 hover:text-blue-600 bg-white p-2 w-10 h-10 rounded-full shadow-[0_6px_20px_rgba(0,0,0,0.25)] transition-all duration-200 z-10"
              title="Scroll to bottom"
            >
              <ChevronDown size={20} className="m-auto" />
            </button>
          )}
        </main>

        <footer className="shrink-0 p-4 bg-white border-t border-gray-200 rounded-b-xl">
          {conversationId && (
            <>
              {showMediaUpload && (
                <div className="mb-4">
                  <MediaUpload
                    onUpload={handleMediaUpload}
                    onCancel={() => setShowMediaUpload(false)}
                    disabled={isLoading}
                    className="w-full"
                    config={config}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={
                    isAgentMode
                      ? 'Type your message...'
                      : loadingStatus || config.inputPlaceholder || 'Ask me anything...'
                  }
                  disabled={isLoading || showMediaUpload}
                  className="flex-1 px-4 bg-white py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 text-sm text-gray-600 min-w-0"
                  style={{ ['--tw-ring-color' as any]: primaryColor }}
                />

                {(isAgentMode ||
                  (messages.length > 0 &&
                    messages[messages.length - 1].type === 'workflow' &&
                    messages[messages.length - 1].workflowData?.messageType === 'media_question')) && (
                    <button
                      type="button"
                      onClick={() => setShowMediaUpload((value) => !value)}
                      disabled={isLoading}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                      title="Attach file"
                    >
                      <Paperclip size={18} />
                    </button>
                  )}

                <button
                  type="button"
                  onClick={() => handleSendMessage()}
                  disabled={isLoading || !inputValue.trim() || showMediaUpload}
                  className="px-3 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                >
                  <Send size={18} />
                </button>
              </div>
            </>
          )}

          <div className="text-gray-400 font-medium text-xs flex items-center justify-center gap-2 mt-4">
            <span className="uppercase">Powered by</span>
            <a
              href={import.meta.env.VITE_POWERED_BY_LINK || 'https://thequickassist.co'}
              className="font-medium flex items-center gap-1 hover:underline transition-all delay-200 duration-200 ease-in-out"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3967fe' }}
            >
              <img
                src={poweredByIcon}
                alt="powered by thequickassist.co"
                className="w-3.5 h-3.5"
                height={100}
                width={100}
              />
              <span>TheQuickAssist.co</span>
            </a>
          </div>
        </footer>

        <BottomModal
          isOpen={showDataCollection}
          onClose={() => setShowDataCollection(false)}
          title="Please provide your information"
          sections={[]}
          selectedItem={null}
          onItemSelect={() => undefined}
          showSearch={false}
          containerPositioned
          customContent={
            <div className="px-4 py-4">
              <div className="space-y-4 max-h-[40vh] overflow-y-auto">
                {config.dataCollection?.map((field) => (
                  <div key={field._id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type={
                        field.type === 'email'
                          ? 'email'
                          : field.type === 'phone'
                            ? 'tel'
                            : field.type === 'number'
                              ? 'number'
                              : 'text'
                      }
                      value={collectedData[field._id] || ''}
                      onChange={(event) =>
                        setCollectedData((prev) => ({ ...prev, [field._id]: event.target.value }))
                      }
                      placeholder={field.placeholder || `Enter your ${field.label.toLowerCase()}`}
                      required={field.required}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent text-sm"
                      style={{ ['--tw-ring-color' as any]: primaryColor }}
                    />
                  </div>
                ))}
              </div>
            </div>
          }
          footerButtons={[
            {
              label: 'Cancel',
              onClick: () => setShowDataCollection(false),
              variant: 'secondary',
              className: 'flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium',
            },
            {
              label: isSubmittingData ? 'Submitting...' : 'Submit',
              onClick: handleDataCollectionSubmit,
              variant: 'primary',
              disabled: isSubmittingData,
              style: { backgroundColor: primaryColor },
              className: 'flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium',
            },
          ]}
        />
      </div>
    </div>
  );
}
